import React, { useCallback, useState } from 'react';
import {
  CloseButton,
  CollapsedButton,
  Head,
  Hint,
  Label,
  List,
  MouseSvg,
  Panel,
  Row,
  Title,
  Wrapper,
} from './NavLegend.styled';

const LS_KEY = 'proc-nav-legend-collapsed';

type MouseVariant = 'left' | 'middle' | 'right';

interface MouseGlyphProps {
  readonly variant: MouseVariant;
}

// Each variant highlights one mouse region via the CSS custom property
// `--glyph-accent` set on `MouseSvg` (reads `primaryFixedDim` from the theme).
const ACCENT = 'var(--glyph-accent)';
const CLEAR = 'transparent';

const FILLS: Record<MouseVariant, { L: string; M: string; R: string }> = {
  left: { L: ACCENT, M: CLEAR, R: CLEAR },
  middle: { L: CLEAR, M: ACCENT, R: CLEAR },
  right: { L: CLEAR, M: CLEAR, R: ACCENT },
};

const MouseGlyph: React.FC<MouseGlyphProps> = ({ variant }) => {
  const fill = FILLS[variant];
  return (
    <MouseSvg viewBox="0 0 20 26" aria-hidden="true">
      <rect
        x="2"
        y="2"
        width="16"
        height="22"
        rx="8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity="0.85"
      />
      <line
        x1="2.6"
        y1="11"
        x2="17.4"
        y2="11"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.55"
      />
      <path
        d="M2.6 10.6 L2.6 6 A7.4 7.4 0 0 1 9.3 2.7 L9.3 10.6 Z"
        fill={fill.L}
        opacity="0.9"
      />
      <path
        d="M17.4 10.6 L17.4 6 A7.4 7.4 0 0 0 10.7 2.7 L10.7 10.6 Z"
        fill={fill.R}
        opacity="0.9"
      />
      <rect
        x="9"
        y="4.4"
        width="2"
        height="5.2"
        rx="1"
        fill={fill.M}
        stroke="currentColor"
        strokeWidth="0.9"
        opacity="0.95"
      />
    </MouseSvg>
  );
};

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
};

const NavLegend: React.FC = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(LS_KEY, next ? '1' : '0');
      } catch {
        /* localStorage unavailable */
      }
      return next;
    });
  }, []);

  if (collapsed) {
    return (
      <Wrapper>
        <CollapsedButton
          type="button"
          onClick={toggle}
          title="Show navigation shortcuts"
          aria-label="Show navigation shortcuts"
          data-testid="nav-legend-collapsed"
        >
          <span className="material-symbols-rounded">mouse</span>
        </CollapsedButton>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Panel
        role="note"
        aria-label="Canvas navigation shortcuts"
        data-testid="nav-legend"
      >
        <Head>
          <Title>Navigation</Title>
          <CloseButton
            type="button"
            onClick={toggle}
            title="Hide"
            aria-label="Collapse legend"
            data-testid="nav-legend-close"
          >
            <span className="material-symbols-rounded">close</span>
          </CloseButton>
        </Head>
        <List>
          <Row>
            <MouseGlyph variant="left" />
            <Label>Select</Label>
          </Row>
          <Row>
            <MouseGlyph variant="middle" />
            <Label>
              Zoom <Hint>(scroll)</Hint>
            </Label>
          </Row>
          <Row>
            <MouseGlyph variant="right" />
            <Label>
              Pan <Hint>(drag)</Hint>
            </Label>
          </Row>
        </List>
      </Panel>
    </Wrapper>
  );
};

export default NavLegend;
